import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { postJson, AuthServerError, type AuthSession } from '@/lib/authServer';
import { supabase } from '@/lib/supabase';

const STORAGE_KEY = 'mb_auth';
const REFRESH_BEFORE_EXPIRY_S = 120;
const REFRESH_CHECK_INTERVAL_MS = 30_000;

function readStored(): AuthSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

function writeStored(s: AuthSession | null): void {
  if (s) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

async function mirrorIntoSupabase(s: AuthSession): Promise<void> {
  await supabase.auth.setSession({
    access_token: s.accessToken,
    refresh_token: s.refreshToken,
  });
}

interface AuthContextValue {
  session: AuthSession | null;
  user: AuthSession['user'] | null;
  loading: boolean;
  setSession: (s: AuthSession) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const applySession = useCallback(async (s: AuthSession | null) => {
    writeStored(s);
    setSessionState(s);
    if (s) await mirrorIntoSupabase(s);
  }, []);

  const setSession = useCallback(
    async (s: AuthSession) => {
      await applySession(s);
    },
    [applySession],
  );

  // Boot: read session from localStorage, mirror into supabase-js for DB queries
  useEffect(() => {
    const stored = readStored();
    if (!stored) {
      setLoading(false);
      return;
    }
    const now = Math.floor(Date.now() / 1000);
    if (stored.expiresAt <= now) {
      // Already expired — attempt refresh before giving up
      postJson<AuthSession>('/auth/refresh', { refreshToken: stored.refreshToken })
        .then((refreshed) => applySession(refreshed))
        .catch(() => applySession(null))
        .finally(() => setLoading(false));
    } else {
      void mirrorIntoSupabase(stored).finally(() => {
        setSessionState(stored);
        setLoading(false);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic refresh: call /auth/refresh when within REFRESH_BEFORE_EXPIRY_S of expiry
  useEffect(() => {
    refreshTimer.current = setInterval(() => {
      setSessionState((current) => {
        if (!current) return current;
        const now = Math.floor(Date.now() / 1000);
        if (current.expiresAt - now <= REFRESH_BEFORE_EXPIRY_S) {
          postJson<AuthSession>('/auth/refresh', { refreshToken: current.refreshToken })
            .then((refreshed) => applySession(refreshed))
            .catch(() => applySession(null));
        }
        return current;
      });
    }, REFRESH_CHECK_INTERVAL_MS);

    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
  }, [applySession]);

  const signOut = useCallback(async () => {
    const current = readStored();
    if (current) {
      // Best-effort server-side revoke
      await postJson('/auth/signout', {}, current.accessToken).catch((err: unknown) => {
        if (err instanceof AuthServerError && err.code === 'Unauthorized') return;
        // Ignore network errors on signout — local wipe still proceeds
      });
    }
    await applySession(null);
    // Clear supabase-js local state so DB queries stop using stale token
    await supabase.auth.signOut({ scope: 'local' });
  }, [applySession]);

  return (
    <AuthContext.Provider
      value={{ session, user: session?.user ?? null, loading, setSession, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
